import { useEffect, useReducer } from 'react';
import { useToasts } from 'react-toast-notifications';

import axios, { AxiosRequestConfig } from 'axios';
import { request } from 'redux/api';

import { useTimeout } from './use-timeout';

type RequestAction = { type: 'FETCH_REQUEST' };
type SuccessAction<S> = { type: 'FETCH_SUCCESS'; payload: S };
type FailureAction<E> = { type: 'FETCH_FAILURE'; payload: E };

type Action<S, E> = RequestAction | SuccessAction<S> | FailureAction<E>;

interface RequestState<T, U> {
  isLoading: boolean;
  isFailed: boolean;
  data: T | undefined;
  error: U | undefined;
}

const dataFetchReducer = <T, U>(state: RequestState<T, U>, action: Action<T, U>) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, data: undefined, isLoading: true, isFailed: false };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isFailed: false,
        data: action.payload,
      };
    case 'FETCH_FAILURE':
      return {
        ...state,
        isLoading: false,
        isFailed: true,
        error: action.payload,
      };
    default:
      throw new Error();
  }
};

const useAPI = <T, U = any>(config: AxiosRequestConfig, shouldFetch = true, initialData?: T) => {
  const { addToast } = useToasts();

  const [state, dispatch] = useReducer<
    (state: RequestState<T, U>, action: Action<T, U>) => RequestState<T, U>
  >(dataFetchReducer, {
    isLoading: false,
    isFailed: false,
    data: initialData,
    error: undefined,
  });

  useEffect(() => {
    let didCancel = false;
    const source = axios.CancelToken.source();

    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });

      try {
        const result = await request<T>({
          ...config,
          cancelToken: source.token,
        });
        if (!didCancel) {
          dispatch({ type: 'FETCH_SUCCESS', payload: result });
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Request canceled', error.message);
        } else if (!didCancel) {
          dispatch({ type: 'FETCH_FAILURE', payload: error });
          addToast(error.response ? error.response.data.message : error.message);
        }
      }
    };

    if (shouldFetch) {
      fetchData();
    }

    return () => {
      didCancel = true;
      source.cancel('Request cancelled, component is unmounted!');
    };
  }, [addToast, config, shouldFetch]);

  return state;
};

export default useAPI;

export const useMockAPI = <T, U = any>(data: T, delay = 1000) => {
  const [state, dispatch] = useReducer<
    (state: RequestState<T, U>, action: Action<T, U>) => RequestState<T, U>
  >(dataFetchReducer, {
    isLoading: true,
    isFailed: false,
    data: undefined,
    error: undefined,
  });

  useTimeout(() => {
    dispatch({ type: 'FETCH_SUCCESS', payload: data });
  }, delay);

  return state;
};
